import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LsqEntity } from '@lsq-frontend/remote-services/enterprise/common';
import {
    AmericanStateAbbreviations,
    AmericanStateOptions,
    forLifeOf,
    LifecycleAware,
    Masks,
    Unmasks,
    LsqValidators
} from '@lsq-frontend/common-ui';
import { combineLatest, Observable, Subject } from 'rxjs';
import { first, map, shareReplay, startWith } from 'rxjs/operators';
import { CreateOrUpdateBuyerImplementationCompanyLocationService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/create-or-update-buyer-implementation-company-location';
import {
    GetBuyerImplementationCompanyLocationResponse,
    GetBuyerImplementationCompanyLocationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer-implementation/get-buyer-implementation-company-location';
import { CreateOrUpdateBuyerCompanyLocationService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/create-or-update-buyer-company-location';
import {
    GetBuyerCompanyLocationResponse,
    GetBuyerCompanyLocationService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-manage-buyer/get-buyer-company-location';
import { ManageBuyerService } from '../manage-buyer.service';
import { AdminManageBuyerInformationChanges } from '../modals/confirm-changes-modal.component';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';

@LifecycleAware()
@Component({
    selector: 'admin-manage-buyer-company-location',
    template: `
        <div class="row">
            <div class="col-12 col-md-8">
                <card headerTitle="Buyer Location">
                    <div card-body *ngIf="!initialized">
                        <lsq-circle-loader></lsq-circle-loader>
                    </div>
                    <div
                        card-body
                        [style.padding]="'2rem 3rem 3rem'"
                        [style.marginBottom]="'2rem'"
                        *ngIf="initialized"
                    >
                        <form [formGroup]="formGroup" (ngSubmit)="saveChanges()">
                            <div class="row">
                                <div class="col-12">
                                    <lsq-label
                                        label="Office Name*"
                                        subLabel="The name for the physical office. Headquarters should be the most common name."
                                    ></lsq-label>
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="officeName"
                                        formControlName="officeName"
                                        [group]="formGroup"
                                    >
                                    </lsq-input>
                                </div>
                                <div class="col-12">
                                    <lsq-label
                                        label="Country Code*"
                                        subLabel="This defines which country the address is in."
                                    ></lsq-label>
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="countryCode"
                                        formControlName="countryCode"
                                        [group]="formGroup"
                                    ></lsq-input>
                                </div>
                                <div class="col-12">
                                    <lsq-label
                                        label="Address*"
                                        subLabel="The address for the buyer's main place of business."
                                    ></lsq-label>
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="addressLine1"
                                        formControlName="addressLine1"
                                        [group]="formGroup"
                                    >
                                    </lsq-input>
                                </div>
                                <div class="col-12">
                                    <lsq-label
                                        label="Address Line 2"
                                        subLabel="Optional. The second line for the address of the buyer if needed."
                                    ></lsq-label>
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="addressLine2"
                                        formControlName="addressLine2"
                                        [group]="formGroup"
                                    >
                                    </lsq-input>
                                </div>
                                <div class="col-12">
                                    <lsq-label
                                        label="City*"
                                        subLabel="The city for the buyer's main place of business."
                                    ></lsq-label>
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="city"
                                        formControlName="city"
                                        [group]="formGroup"
                                    >
                                    </lsq-input>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 col-md-6 order-md-1">
                                    <lsq-label
                                        label="State*"
                                        subLabel="The state for the buyer's main place of business."
                                    ></lsq-label>
                                </div>
                                <div class="col-12 col-md-6 order-md-3">
                                    <lsq-select
                                        [basicLabel]="true"
                                        name="state"
                                        [options]="americanStateOptions"
                                        formControlName="state"
                                        [group]="formGroup"
                                    ></lsq-select>
                                </div>
                                <div class="col-12 col-md-6 order-md-2">
                                    <lsq-label
                                        label="Zip Code*"
                                        subLabel="The zip code for the buyer's main place of business."
                                    ></lsq-label>
                                </div>
                                <div class="col-12 col-md-6 order-md-4">
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="zipCode"
                                        formControlName="zipCode"
                                        [group]="formGroup"
                                        [maxLength]="10"
                                    >
                                    </lsq-input>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12">
                                    <lsq-label
                                        label="LSQ Entity*"
                                        subLabel="This determines whether to use LSQ as the company entity or LS DE for buyers in the state of California."
                                    ></lsq-label>
                                    <lsq-select
                                        [basicLabel]="true"
                                        name="lsqEntity"
                                        formControlName="lsqEntity"
                                        [options]="lsqEntityOptions"
                                        [group]="formGroup"
                                    ></lsq-select>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 col-md-6 order-md-1">
                                    <lsq-label
                                        label="Location Phone Number"
                                        subLabel="The location’s main 10-digit phone line."
                                    ></lsq-label>
                                </div>
                                <div class="col-12 col-md-6 order-md-3">
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="phoneNumber"
                                        [mask]="masks.PhoneMask"
                                        type="tel"
                                        [unmask]="phoneUnmask"
                                        formControlName="phoneNumber"
                                        [group]="formGroup"
                                    >
                                    </lsq-input>
                                </div>
                                <div class="col-12 col-md-6 order-md-2">
                                    <lsq-label
                                        label="Location Email Address"
                                        subLabel="The buyer's email address for this location."
                                    ></lsq-label>
                                </div>
                                <div class="col-12 col-md-6 order-md-4">
                                    <lsq-input
                                        [basicLabel]="true"
                                        name="emailAddress"
                                        formControlName="emailAddress"
                                        [group]="formGroup"
                                    >
                                    </lsq-input>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-12 col-md-3" [style.marginTop]="'2rem'">
                                    <lsq-button
                                        [style.marginRight]="'2rem'"
                                        appearance="secondary"
                                        size="block"
                                        [disabled]="(changes$ | async)?.length === 0 || submitting"
                                        (buttonClick)="resetForm()"
                                    >
                                        Clear Changes
                                    </lsq-button>
                                </div>
                                <div class="col-12 col-md-3" [style.marginTop]="'2rem'">
                                    <lsq-button
                                        appearance="primary"
                                        size="block"
                                        type="submit"
                                        [submitting]="submitting"
                                        [disabled]="(changes$ | async)?.length === 0"
                                    >
                                        Save
                                    </lsq-button>
                                </div>
                            </div>
                        </form>
                    </div>
                </card>
            </div>
            <div class="col-12 col-md-4">
                <data-card [lightBorder]="true">
                    <p class="header" [style.marginBottom]="'1rem'">Additional Information</p>
                    <p class="body text-dark-gray" [style.marginBottom]="'1rem'">
                        Please enter your updated information for the buyer and once you are done
                        click the Save button.
                    </p>
                </data-card>
            </div>
        </div>
    `
})
export class AdminManageBuyerCompanyLocationComponent implements OnInit {
    public readonly masks = Masks;
    public readonly phoneUnmask = Unmasks.PhoneUnmask;
    public americanStateOptions = [{ value: null, label: '-' }].concat(AmericanStateOptions);
    public lsqEntityOptions = [
        {
            value: LsqEntity.Lsq,
            label: 'LSQ'
        },
        {
            value: LsqEntity.LsDe,
            label: 'LS DE'
        }
    ];

    public initialized = false;
    public submitting = false;
    private _cachedResponse:
        | GetBuyerCompanyLocationResponse
        | GetBuyerImplementationCompanyLocationResponse;

    public formGroup = new FormGroup({
        officeName: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        countryCode: new FormControl({ value: 'USA', disabled: true }, [
            Validators.required,
            LsqValidators.maxLength(255)
        ]),
        addressLine1: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        addressLine2: new FormControl(null, [LsqValidators.maxLength(255)]),
        city: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        state: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        zipCode: new FormControl(null, [Validators.required, LsqValidators.maxLength(255)]),
        phoneNumber: new FormControl(null, [LsqValidators.phone]),
        emailAddress: new FormControl(null, [LsqValidators.maxLength(255), LsqValidators.email]),
        lsqEntity: new FormControl({ value: LsqEntity.Lsq, disabled: true })
    });

    private _refetchSubject = new Subject();

    public changes$: Observable<
        AdminManageBuyerInformationChanges[]
    > = this.formGroup.valueChanges.pipe(
        map((_) => {
            const changesLabels = {
                officeName: 'Office name',
                addressLine1: 'Address',
                addressLine2: 'Address Line 2',
                countryCode: 'Country Code',
                city: 'City',
                state: 'State',
                zipCode: 'ZipCode',
                postalCode: 'Postal Code',
                phoneNumber: 'Location Phone Number',
                emailAddress: 'Location Email Address'
            };

            return Object.keys(this.formGroup.value).reduce((changes, formGroupKey) => {
                if (this.formGroup.value[formGroupKey] !== this._cachedResponse[formGroupKey]) {
                    changes.push({
                        label: changesLabels[formGroupKey],
                        newData: this.formGroup.value[formGroupKey],
                        oldData: this._cachedResponse[formGroupKey]
                    });
                }
                return changes;
            }, []);
        }),
        startWith([]),
        shareReplay(1)
    );

    constructor(
        private _getBuyerCompanyLocationService: GetBuyerCompanyLocationService,
        private _getBuyerImplementationCompanyLocationService: GetBuyerImplementationCompanyLocationService,
        private _updateBuyerCompanyLocationService: CreateOrUpdateBuyerCompanyLocationService,
        private _updateBuyerImplementationCompanyLocationService: CreateOrUpdateBuyerImplementationCompanyLocationService,
        private _manageBuyerService: ManageBuyerService,
        // private _modalService: ModalService,
        private _alertService: AlertService
    ) {}

    public ngOnInit(): void {
        combineLatest([
            this._manageBuyerService.isImplementation$,
            this._manageBuyerService.buyerKey$,
            this._refetchSubject.pipe(startWith(true))
        ])
            .pipe(forLifeOf(this))
            .subscribe(async ([isImplementation, buyerKey, _]) => {
                this.initialized = false;

                if (isImplementation) {
                    this._cachedResponse = await this._getBuyerImplementationCompanyLocationService.executeOnce(
                        {
                            buyerImplementationKey: buyerKey
                        }
                    );
                } else {
                    this._cachedResponse = await this._getBuyerCompanyLocationService.executeOnce({
                        buyerKey
                    });
                }

                this.resetForm();
                this.initialized = true;
            });

        this.formGroup
            .get('state')
            .valueChanges.pipe(forLifeOf(this))
            .subscribe((value) => {
                if (value === AmericanStateAbbreviations.California) {
                    this.formGroup.get('lsqEntity').setValue(LsqEntity.LsDe);
                } else {
                    this.formGroup.get('lsqEntity').setValue(LsqEntity.Lsq);
                }
            });
    }

    public async commitChanges(): Promise<void> {
        this.submitting = true;
        const isImplementation = await this._manageBuyerService.isImplementation$
            .pipe(first())
            .toPromise();
        try {
            if (isImplementation) {
                await this._updateBuyerImplementationCompanyLocationService.executeOnce({
                    ...this.formGroup.value,
                    countryCode: 'USA',
                    buyerImplementationKey: await this._manageBuyerService.buyerKey$
                        .pipe(first())
                        .toPromise()
                });
            } else {
                await this._updateBuyerCompanyLocationService.executeOnce({
                    ...this.formGroup.value,
                    buyerKey: await this._manageBuyerService.buyerKey$.pipe(first()).toPromise()
                });
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this._alertService.success('Buyer location successfully updated.');
            this._refetchSubject.next();
        } catch {
            this._alertService.error('An error ocurred while updating.');
        }

        this.submitting = false;
    }

    public async saveChanges(): Promise<void> {
        this.formGroup.markAllAsTouched();

        if (this.formGroup.valid) {
            await this.commitChanges();

            // TODO: Either uncomment or remove code, waiting on decision from product if this modal is needed or not.
            // const _isSubmittingSubject = new BehaviorSubject<boolean>(false);
            // this._modalService.showModal(
            //     new Modal({
            //         title: 'Update Company Location',
            //         bodyComponent: AdminManageBuyerConfirmChangesModalComponent,
            //         isSubmitting$: _isSubmittingSubject.asObservable(),
            //         showFooter: true,
            //         showCancelButton: true,
            //         showCloseButton: true,
            //         showOkButton: true,
            //         width: '700px',
            //         okButtonSize: 'block',
            //         okButtonText: 'Confirm Update',
            //         data: {
            //             changes: await this.changes$.pipe(first()).toPromise()
            //         },
            //         onOkClick: async () => {
            //             _isSubmittingSubject.next(true);
            //             try {
            //                 await this.commitChanges();
            //                 this._modalService.closeModal();
            //             } catch {
            //                 this._alertService.success('An error ocurred while updating.');
            //             }
            //             _isSubmittingSubject.next(false);
            //         }
            //     })
            // );
        }
    }

    public resetForm(): void {
        this.formGroup.reset({
            ...this._cachedResponse,
            countryCode: 'USA',
            // Some phone numbers have been saved with parentheses and dashes.
            phoneNumber: this._cachedResponse.phoneNumber?.replace(this.phoneUnmask, '')
        });
        if (this._cachedResponse.state === AmericanStateAbbreviations.California) {
            this.formGroup.get('lsqEntity').setValue(LsqEntity.LsDe);
        } else {
            this.formGroup.get('lsqEntity').setValue(LsqEntity.Lsq);
        }
    }
}
